"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ColleyvilleFortWorthFocus() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const dishes = [{
    course: "ARBORICULTURE",
    name: "Tree Health Assessment",
    description: "Comprehensive diagnostic evaluations to ensure the longevity and vitality of your landscape's canopy.",
    price: "From $150",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/tree_health_assessment.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/tree_health_assessment.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/tree_health_assessment.png-wvc-sizes",
    alt: "Arborist inspecting a mature oak tree"
  }, {
    course: "MAINTENANCE",
    name: "Precision Pruning",
    description: "Expert structural trimming to promote healthy growth, improve aesthetics, and mitigate potential hazards.",
    price: "From $300",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/precision_pruning.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/precision_pruning.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/precision_pruning.png-wvc-sizes",
    alt: "Professional arborist pruning branches"
  }, {
    course: "REMOVAL",
    name: "Safe Tree Removal",
    description: "Controlled, systematic removal of hazardous or unwanted trees using industry-leading safety protocols.",
    price: "Custom Quote",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/safe_tree_removal.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/safe_tree_removal.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/safe_tree_removal.png-wvc-sizes",
    alt: "Safe removal of a large tree in a residential area"
  }];
  return <section data-section-id="1002" id="colleyville-fort-worth-focus" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")"
    }} />

      <div className="relative mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16">
        {}
        <div ref={headerRef} className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-end">
          {}
          <div aria-hidden="true" className="pointer-events-none absolute -top-16 md:-top-24 left-0 lg:left-[-1rem] select-none" style={{
          color: "var(--primary)",
          opacity: 0.09
        }}>
            <span className="font-default leading-none" style={{
            fontSize: "clamp(14rem, 28vw, 28rem)",
            WebkitTextStroke: "2px currentColor",
            color: "transparent",
            fontWeight: 400
          }}>
              03
            </span>
          </div>

          {}
          <div className="relative lg:col-span-9">
            <p className={`font-default text-[0.7rem] md:text-[0.8125rem] tracking-[0.18em] uppercase mb-6 md:mb-8 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`} style={{
            color: "var(--secondary)"
          }}>
              — Nº 03 · OUR SERVICES
            </p>
            <h2 className={`font-default font-normal leading-[1.02] -tracking-[0.02em] text-foreground transition-all duration-700 ease-out delay-100 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            fontSize: "clamp(2.25rem, 5vw, 4.25rem)"
          }}>
              Professional{" "}
              <em className={`italic transition-all duration-600 ease-out delay-300 inline-block ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{
              color: "var(--accent)",
              fontWeight: 380
            }}>
                Arboriculture
              </em>
            </h2>
          </div>

          {}
          <div className={`lg:col-span-3 lg:pb-3 transition-all duration-700 ease-out delay-200 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <Link to="/OurExpertisePage" className="group inline-flex items-baseline gap-2 font-default text-base border-b pb-1 transition-colors" style={{
            color: "var(--accent)",
            borderColor: "var(--accent)"
          }}>
              <span>View all services</span>
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-28 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {dishes.map((dish, i) => <article key={i} data-index={i} className="group relative bg-card rounded-lg p-5 shadow-md hover:shadow-xl transition-all duration-[320ms] ease-[cubic-bezier(.2,.7,.2,1)] hover:-translate-y-1">
              {}
              <div className="relative overflow-hidden rounded-lg">
                <div className="aspect-square overflow-hidden">
                  <img src={dish.src} data-wvc-srcset={dish.srcset} data-wvc-sizes={dish.sizes} alt={dish.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[320ms] ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.04]" />
                </div>
                {}
                <span className="absolute top-3 left-3 inline-flex items-center font-default text-[0.7rem] tracking-[0.16em] uppercase px-2.5 py-1 rounded-lg border bg-background/90 backdrop-blur-sm" style={{
              color: "var(--primary)",
              borderColor: "var(--primary)"
            }}>
                  {dish.course}
                </span>
              </div>

              {}
              <div className="pt-6">
                <h3 className="font-default font-medium -tracking-[0.01em] leading-tight text-foreground" style={{
              fontSize: "clamp(1.15rem, 1.6vw, 1.375rem)"
            }}>
                  {dish.name}
                </h3>
                <p className="font-default text-base leading-[1.65] mt-3" style={{
              color: "var(--secondary)"
            }}>
                  {dish.description}
                </p>

                {}
                <div className="flex items-center justify-start py-5" aria-hidden="true">
                  <svg width="32" height="14" viewBox="0 0 32 14" fill="none" xmlns="http://www.w3.org/2000/svg" style={{
                color: "var(--secondary)"
              }}>
                    <path d="M1 7C3 2 6 2 8 7C10 12 13 12 15 7C17 2 20 2 22 7C24 12 27 12 29 7C30 5 30.5 4 31 3.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" fill="none" />
                  </svg>
                </div>

                <div className="flex items-baseline justify-between">
                  <span className="font-default text-sm tracking-[0.04em]" style={{
                color: "var(--accent)"
              }}>
                    {dish.price}
                  </span>
                </div>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}